import { Accordion, AccordionDetails, AccordionSummary, Button, IconButton, InputAdornment, Link, MenuItem, TextField, Tooltip } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import EditOutlined from '@material-ui/icons/EditOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import WarningIcon from '@material-ui/icons/Warning';
import _ from 'lodash';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  buildSecretField,
  DomainSecretFieldType,
  IDomainSecretFieldInDTO,
  maskPasswordValue,
  parseSecretsFromApi,
  serializeSecretsForApi,
} from '../../utils/domainSecretsUtils';
import { ModalTypes } from '../Modals/ModalTypes';
import * as ModalsActions from '../../redux/modals/modals.actions';
import { fetchPlantSecrets, savePlantSecrets } from '../../repository/jago/domainsAPI';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { Roles } from '../../services/PermissionService/PermissionConstants';
import Permission from '../Permission/Permission';
import ReduxLanguage from '../ReduxLanguage';

const DELETE_DOMAIN_SECRET_FIELD_MODAL_ID = 'DELETE_DOMAIN_SECRET_FIELD_MODAL';

const DOMAIN_SECRETS_MANAGE_ROLES = [
  Roles.ROLE_VASHO_SOFIA_ADMIN,
  Roles.ROLE_VASHO_GLOBAL_SOC_MANAGER,
];

const FIELD_TYPE_OPTIONS: DomainSecretFieldType[] = ['text', 'password', 'url', 'boolean'];

const FIELD_TYPE_LABEL_KEYS: Record<DomainSecretFieldType, string> = {
  text: 'domains.domainSecretsFieldTypeText',
  password: 'domains.domainSecretsFieldTypePassword',
  url: 'domains.domainSecretsFieldTypeUrl',
  boolean: 'domains.domainSecretsFieldTypeBoolean',
};

const styles = createStyles({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  sectionSubtitle: {
    fontWeight: 100,
    lineHeight: '1.5em',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    borderBottom: '1px solid #eeeeee',
    paddingTop: 10,
    paddingBottom: 10,
    gap: 16,
  },
  label: {
    fontWeight: 'bold',
    minWidth: 180,
    color: '#3f3f3f',
  },
  value: {
    flex: 1,
    wordBreak: 'break-word',
    textAlign: 'right',
  },
  valueLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  iconGroup: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 12,
  },
  secretIcon: {
    padding: 6,
    color: '#607D8B',
    '&:hover': {
      color: '#0D47A1',
      backgroundColor: 'rgba(13, 71, 161, 0.08)',
    },
  },
  secretIconCopied: {
    padding: 6,
    color: '#5AC0B1',
  },
  secretIconDelete: {
    padding: 6,
    color: '#607D8B',
    '&:hover': {
      color: '#c62828',
      backgroundColor: 'rgba(198, 40, 40, 0.08)',
    },
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 8,
    marginBottom: 8,
    paddingLeft: 12,
    borderLeft: '3px solid #0D47A1',
  },
  groupTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statusText: {
    color: '#3f3f3f',
  },
  addRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 16,
    gap: 10,
    width: '100%',
  },
  editBlock: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderBottom: '1px solid #eeeeee',
    paddingTop: 10,
    paddingBottom: 12,
    gap: 8,
  },
  editRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    gap: 8,
  },
  editField: {
    flex: 1,
    minWidth: 200,
  },
  actionButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  fieldTypeSelect: {
    minWidth: 150,
  },
  saveError: {
    color: 'red',
    marginTop: 8,
  },
});

type IDomainSecretsViewReduxProps = ConnectedProps<typeof connector>;

export interface IDomainSecretsViewProps extends WithStyles<typeof styles>, IDomainSecretsViewReduxProps {
  plantId?: number;
}

interface IDomainSecretsViewState {
  expanded: boolean;
  loading: boolean;
  loaded: boolean;
  error: boolean;
  secrets: Record<string, IDomainSecretFieldInDTO> | null;
  copiedKey: string | null;
  newKey: string;
  newValue: string;
  newFieldType: DomainSecretFieldType;
  editingPath: string | null;
  editingValue: string;
  editingFieldType: DomainSecretFieldType;
  visiblePasswordPaths: Record<string, boolean>;
  saving: boolean;
  saveError: boolean;
}

class DomainSecretsView extends React.Component<IDomainSecretsViewProps, IDomainSecretsViewState> {
  constructor(props: IDomainSecretsViewProps) {
    super(props);
    this.state = {
      expanded: false,
      loading: false,
      loaded: false,
      error: false,
      secrets: null,
      copiedKey: null,
      newKey: '',
      newValue: '',
      newFieldType: 'text',
      editingPath: null,
      editingValue: '',
      editingFieldType: 'text',
      visiblePasswordPaths: {},
      saving: false,
      saveError: false,
    };
  }

  public componentDidUpdate(prevProps: IDomainSecretsViewProps) {
    if (prevProps.plantId !== this.props.plantId) {
      this.setState({
        expanded: false,
        loading: false,
        loaded: false,
        error: false,
        secrets: null,
        copiedKey: null,
        newKey: '',
        newValue: '',
        newFieldType: 'text',
        editingPath: null,
        editingValue: '',
        editingFieldType: 'text',
        visiblePasswordPaths: {},
        saving: false,
        saveError: false,
      });
    }
  }

  private canManageDomainSecrets(): boolean {
    return AbilityProvider.getAbilityHelper().hasRoles(DOMAIN_SECRETS_MANAGE_ROLES);
  }

  private async persistSecrets(fields: Record<string, IDomainSecretFieldInDTO>) {
    const { plantId } = this.props;
    if (!plantId) return;
    const payload = serializeSecretsForApi(fields);
    await savePlantSecrets(plantId, payload);
    this.setState({ secrets: _.isEmpty(payload) ? null : payload });
  }

  public async onToggle(_event: React.ChangeEvent<{}>, expanded: boolean) {
    const { plantId } = this.props;
    const { loaded, loading } = this.state;
    this.setState({ expanded });
    if (!expanded || loaded || loading || !plantId) return;
    this.setState({ loading: true, error: false });
    try {
      const response = await fetchPlantSecrets(plantId);
      const secrets = response.status === 204 || _.isEmpty(response.data)
        ? null
        : parseSecretsFromApi(response.data as Record<string, unknown>);
      this.setState({ secrets, loaded: true, loading: false });
    } catch (error) {
      this.setState({ error: true, loaded: true, loading: false });
    }
  }

  public async onAddKey() {
    if (!this.canManageDomainSecrets()) return;
    const { newKey, newValue, newFieldType, saving, secrets } = this.state;
    const key = _.trim(newKey);
    if (!key || saving) return;
    const nextSecrets = { ...(secrets || {}), [key]: buildSecretField(newFieldType, newValue) };
    this.setState({ saving: true, saveError: false });
    try {
      await this.persistSecrets(nextSecrets);
      this.setState({ newKey: '', newValue: '', newFieldType: 'text', saving: false });
    } catch (error) {
      this.setState({ saving: false, saveError: true });
    }
  }

  public onStartEdit(path: string, field: IDomainSecretFieldInDTO) {
    if (!this.canManageDomainSecrets()) return;
    this.setState({
      editingPath: path,
      editingValue: String(field.value),
      editingFieldType: field.fieldType,
      saveError: false,
    });
  }

  public onCancelEdit() {
    this.setState({ editingPath: null, editingValue: '', editingFieldType: 'text' });
  }

  public async onSaveEdit() {
    if (!this.canManageDomainSecrets()) return;
    const { secrets, editingPath, editingValue, editingFieldType, saving } = this.state;
    if (!secrets || !editingPath || saving) return;
    const key = editingPath.replace(/^secrets\./, '');
    const original = secrets[key];
    const nextSecrets = {
      ...secrets,
      [key]: buildSecretField(editingFieldType, editingValue, original?.value),
    };
    this.setState({ saving: true, saveError: false });
    try {
      await this.persistSecrets(nextSecrets);
      this.setState({ saving: false, editingPath: null, editingValue: '', editingFieldType: 'text' });
    } catch (error) {
      this.setState({ saving: false, saveError: true });
    }
  }

  public onDeleteKeyRequest(key: string) {
    if (!this.canManageDomainSecrets()) return;
    const { dispatch } = this.props;
    const { saving } = this.state;
    if (saving) return;
    dispatch<any>(ModalsActions.showModal(DELETE_DOMAIN_SECRET_FIELD_MODAL_ID, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'domains.domainSecretsDelete',
        successMessageKey: 'domains.domainSecretsDeleteConfirm',
        successMessageProps: { key: _.startCase(key) },
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteKeyConfirm(key),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(DELETE_DOMAIN_SECRET_FIELD_MODAL_ID)),
      },
    }));
  }

  public async onDeleteKeyConfirm(key: string) {
    if (!this.canManageDomainSecrets()) return;
    const { dispatch } = this.props;
    const { secrets, editingPath } = this.state;
    if (!secrets) return;
    const nextSecrets = _.omit(secrets, key) as Record<string, IDomainSecretFieldInDTO>;
    dispatch<any>(ModalsActions.hideModal(DELETE_DOMAIN_SECRET_FIELD_MODAL_ID));
    this.setState({
      saving: true,
      saveError: false,
      editingPath: editingPath === `secrets.${key}` ? null : editingPath,
      editingValue: editingPath === `secrets.${key}` ? '' : this.state.editingValue,
      editingFieldType: editingPath === `secrets.${key}` ? 'text' : this.state.editingFieldType,
    });
    try {
      await this.persistSecrets(nextSecrets);
      this.setState({ saving: false });
    } catch (error) {
      this.setState({ saving: false, saveError: true });
    }
  }

  public async onCopy(key: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      this.setState({ copiedKey: key });
    } catch (error) {
      this.setState({ copiedKey: null });
    }
  }

  public onTogglePasswordVisibility(fieldPath: string) {
    this.setState(prevState => ({
      visiblePasswordPaths: {
        ...prevState.visiblePasswordPaths,
        [fieldPath]: !prevState.visiblePasswordPaths[fieldPath],
      },
    }));
  }

  private renderFieldTypeSelect(
    selected: DomainSecretFieldType,
    onChange: (fieldType: DomainSecretFieldType) => void,
    disabled: boolean,
  ) {
    const { classes } = this.props;
    return (
      <TextField
        className={classes.fieldTypeSelect}
        select
        size="small"
        variant="outlined"
        label={<ReduxLanguage languageKey="domains.domainSecretsFieldType" />}
        value={selected}
        disabled={disabled}
        onChange={event => onChange(event.target.value as DomainSecretFieldType)}
      >
        {_.map(FIELD_TYPE_OPTIONS, option => (
          <MenuItem key={option} value={option}>
            <ReduxLanguage languageKey={FIELD_TYPE_LABEL_KEYS[option]} />
          </MenuItem>
        ))}
      </TextField>
    );
  }

  private renderValueEditor(
    value: string,
    fieldType: DomainSecretFieldType,
    disabled: boolean,
    onChange: (next: string) => void,
    passwordVisibilityKey?: string,
  ) {
    const { classes } = this.props;
    if (fieldType === 'boolean') {
      return (
        <TextField
          className={classes.editField}
          select
          size="small"
          variant="outlined"
          fullWidth
          value={value}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
        >
          <MenuItem value="true"><ReduxLanguage languageKey="domains.domainSecretsYes" /></MenuItem>
          <MenuItem value="false"><ReduxLanguage languageKey="domains.domainSecretsNo" /></MenuItem>
        </TextField>
      );
    }
    const isPassword = fieldType === 'password';
    const passwordVisible = passwordVisibilityKey ? this.state.visiblePasswordPaths[passwordVisibilityKey] : false;
    return (
      <TextField
        className={classes.editField}
        variant="outlined"
        size="small"
        fullWidth
        type={isPassword && !passwordVisible ? 'password' : 'text'}
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        InputProps={isPassword && passwordVisibilityKey ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => this.onTogglePasswordVisibility(passwordVisibilityKey)}
                edge="end"
              >
                {passwordVisible ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
      />
    );
  }

  private renderPrimitive(value: string | number | boolean, fieldType?: DomainSecretFieldType) {
    if (_.isBoolean(value) || fieldType === 'boolean') {
      const boolValue = _.isBoolean(value) ? value : value === 'true';
      return <ReduxLanguage languageKey={boolValue ? 'domains.domainSecretsYes' : 'domains.domainSecretsNo'} />;
    }
    if (_.isNumber(value)) return String(value);
    const stringValue = String(value);
    if (fieldType === 'url' || /^https?:\/\//i.test(stringValue)) {
      return (
        <Link href={stringValue} target="_blank" rel="noopener noreferrer">
          {stringValue}
        </Link>
      );
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(stringValue)) {
      const date = new Date(stringValue);
      if (!Number.isNaN(date.getTime())) return date.toLocaleString();
    }
    return stringValue;
  }

  private renderFields(data: Record<string, IDomainSecretFieldInDTO>, path: string) {
    const { classes } = this.props;
    const { editingPath, editingValue, editingFieldType, saving, visiblePasswordPaths } = this.state;
    const canManage = this.canManageDomainSecrets();
    return _.map(data, (field, key) => {
      const fieldPath = `${path}.${key}`;
      const fieldType = field.fieldType;
      const value = field.value;
      const isEditing = canManage && editingPath === fieldPath;
      if (isEditing) {
        return (
          <div key={fieldPath} className={classes.editBlock}>
            <div className={classes.label}>{_.startCase(key)}</div>
            <div className={classes.editRow}>
              {this.renderFieldTypeSelect(editingFieldType, next => this.setState({ editingFieldType: next, saveError: false }), saving)}
              {this.renderValueEditor(editingValue, editingFieldType, saving, next => this.setState({ editingValue: next, saveError: false }), '__edit__')}
              <Button
                className={classes.actionButton}
                variant="contained"
                size="small"
                disabled={saving}
                style={{ backgroundColor: '#5AC0B1', color: 'white', fontWeight: 'bold' }}
                onClick={() => this.onSaveEdit()}
              >
                <ReduxLanguage languageKey="forms.save" />
              </Button>
              <Button className={classes.actionButton} variant="outlined" size="small" disabled={saving} onClick={() => this.onCancelEdit()}>
                <ReduxLanguage languageKey="forms.cancel" />
              </Button>
            </div>
          </div>
        );
      }
      const copied = this.state.copiedKey === fieldPath;
      const primitiveText = _.isBoolean(value) ? String(value) : String(value ?? '');
      const passwordVisible = visiblePasswordPaths[fieldPath];
      let primitiveDisplay: React.ReactNode;
      if (fieldType === 'password' && !passwordVisible) {
        primitiveDisplay = maskPasswordValue(primitiveText);
      } else {
        primitiveDisplay = this.renderPrimitive(value, fieldType);
      }
      return (
        <div key={fieldPath} className={classes.row}>
          <div className={classes.label}>{_.startCase(key)}</div>
          <div className={classes.value}>
            <div className={classes.valueLine}>
              {primitiveDisplay}
              <div className={classes.iconGroup}>
                {fieldType === 'password' ? (
                  <Tooltip title={<ReduxLanguage languageKey={passwordVisible ? 'domains.domainSecretsHidePassword' : 'domains.domainSecretsShowPassword'} />}>
                    <IconButton className={classes.secretIcon} onClick={() => this.onTogglePasswordVisibility(fieldPath)}>
                      {passwordVisible ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title={<ReduxLanguage languageKey={copied ? 'domains.domainSecretsCopied' : 'forms.copy'} />}>
                  <IconButton className={copied ? classes.secretIconCopied : classes.secretIcon} onClick={() => this.onCopy(fieldPath, primitiveText)}>
                    {copied ? <Check style={{ fontSize: 18 }} /> : <FileCopyOutlined style={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                {canManage ? (
                  <Tooltip title={<ReduxLanguage languageKey="domains.domainSecretsEdit" />}>
                    <IconButton className={classes.secretIcon} disabled={saving} onClick={() => this.onStartEdit(fieldPath, field)}>
                      <EditOutlined style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {canManage ? (
                  <Tooltip title={<ReduxLanguage languageKey="domains.domainSecretsDelete" />}>
                    <IconButton className={classes.secretIconDelete} disabled={saving} onClick={() => this.onDeleteKeyRequest(key)}>
                      <DeleteOutline style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  public render() {
    const { classes, plantId } = this.props;
    const { expanded, loading, error, secrets, newKey, newValue, newFieldType, saving, saveError } = this.state;
    const canManage = this.canManageDomainSecrets();
    const canAddKey = canManage && Boolean(_.trim(newKey)) && !saving;
    if (!plantId) return null;
    return (
      <Permission abilityHelper={AbilityProvider.getAbilityHelper()} roles={[Roles.ROLE_VASHO_SOFIA_ADMIN, Roles.ROLE_VASHO_LOCAL_SOC_MANAGER, Roles.ROLE_VASHO_GLOBAL_SOC_MANAGER]}>
        <Accordion style={{ marginTop: 20 }} expanded={expanded} onChange={(event, isExpanded) => this.onToggle(event, isExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={classes.sectionHeader}>
              <h3 className={classes.sectionTitle}><ReduxLanguage languageKey="domains.domainSecretsTitle" /></h3>
              <h4 className={classes.sectionSubtitle}><ReduxLanguage languageKey="domains.domainSecretsDescription" /></h4>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.body}>
              {loading ? <h4 className={classes.statusText}><ReduxLanguage languageKey="forms.spinnerLoading" /></h4> : null}
              {error ? <h4 className={classes.statusText}><ReduxLanguage languageKey="domains.domainSecretsError" /></h4> : null}
              {!loading && !error && _.isEmpty(secrets) ? <h4 className={classes.statusText}><ReduxLanguage languageKey="domains.domainSecretsEmpty" /></h4> : null}
              {!loading && !error && secrets ? this.renderFields(secrets, 'secrets') : null}
              {!loading && !error && canManage ? (
                <div className={classes.addRow}>
                  <TextField
                    label={<ReduxLanguage languageKey="domains.domainSecretsKey" />}
                    variant="outlined"
                    size="small"
                    value={newKey}
                    disabled={saving}
                    onChange={event => this.setState({ newKey: event.target.value, saveError: false })}
                  />
                  {this.renderFieldTypeSelect(newFieldType, next => this.setState({ newFieldType: next, newValue: next === 'boolean' ? 'false' : '', saveError: false }), saving)}
                  {newFieldType === 'boolean' ? (
                    <TextField
                      className={classes.editField}
                      select
                      size="small"
                      variant="outlined"
                      label={<ReduxLanguage languageKey="domains.domainSecretsValue" />}
                      value={newValue || 'false'}
                      disabled={saving}
                      onChange={event => this.setState({ newValue: event.target.value, saveError: false })}
                    >
                      <MenuItem value="true"><ReduxLanguage languageKey="domains.domainSecretsYes" /></MenuItem>
                      <MenuItem value="false"><ReduxLanguage languageKey="domains.domainSecretsNo" /></MenuItem>
                    </TextField>
                  ) : this.renderValueEditor(newValue, newFieldType, saving, next => this.setState({ newValue: next, saveError: false }), '__new__')}
                  <Button
                    className={classes.actionButton}
                    variant="contained"
                    size="small"
                    disabled={!canAddKey}
                    style={{ backgroundColor: canAddKey ? '#5AC0B1' : undefined, color: 'white', fontWeight: 'bold' }}
                    onClick={() => this.onAddKey()}
                  >
                    <ReduxLanguage languageKey="domains.domainSecretsAdd" />
                  </Button>
                </div>
              ) : null}
              {saveError ? <h4 className={classes.saveError}><ReduxLanguage languageKey="domains.domainSecretsSaveError" /></h4> : null}
            </div>
          </AccordionDetails>
        </Accordion>
      </Permission>
    );
  }
}

const connector = connect();
export default connector(withStyles(styles)(DomainSecretsView));
